'use client';

import { useContext, useEffect, useMemo } from 'react';
import { useI18n } from 'next-localization';

import { FormProvider, useForm } from 'react-hook-form';

import { FormDatePicker } from '@/components/form/form-date-picker';
import { FormInput } from '@/components/form/form-input';
import { FormPhoneInput } from '@/components/form/form-phone-input';
import { FormRadio } from '@/components/form/form-radio';
import { FormSelect } from '@/components/form/form-select';
import {
  CELEBRUS_CONTINUE_BUTTON_ID_3,
  CELEBRUS_PATIENT_ADDRESS_ID,
  CELEBRUS_PATIENT_ADDRESS2_ID,
  CELEBRUS_PATIENT_CITY_ID,
  CELEBRUS_PATIENT_DOB_ID,
  CELEBRUS_PATIENT_EMAIL_ID,
  CELEBRUS_PATIENT_FIRST_NAME_ID,
  CELEBRUS_PATIENT_LAST_NAME_ID,
  CELEBRUS_PATIENT_PHONE_ID,
  CELEBRUS_PATIENT_ZIP_ID,
} from '@/lib/constants';
import { UserDataContext } from '@/providers/user-data-provider';
import { zodResolver } from '@hookform/resolvers/zod';

import { useBookingContext } from '../../booking-context';
import { NavigationButtons } from '../navigation-buttons';
import Section from '../section';
import stateGeoJSON from '../states.json'; // Todo: remove this as it should be imported from shared-lib
import { StepTwoFormKeys } from './constants';
import { EmailTermsAndConditions } from './email-terms-and-conditions';
import { stepTwoSchema } from './schema';
import { StepsWrapper } from './steps-wrapper';
import { getMaxCalendarDate, getValueOrDefault, isUnder18YearsOld } from './utils';

const GENDER_OPTIONS = [
  { label: 'Male', value: 'Male' },
  { label: 'Female', value: 'Female' },
];

export const StepTwo = ({
  goToNextStep,
  goToPreviousStep,
}: {
  goToNextStep: () => void;
  goToPreviousStep: () => void;
}): JSX.Element => {
  const { t } = useI18n();

  const { stepTwoData, setStepTwoData } = useBookingContext();

  const user = useContext(UserDataContext);

  const defaultUserValues = getValueOrDefault(stepTwoData, user);

  const allStatesAbbreviationItems = useMemo(() => {
    return [
      { label: 'Select State', value: '', selected: !defaultUserValues.state },
      ...stateGeoJSON.features
        .map((state) => {
          const abbreviation = state.properties.abbreviation;
          return {
            label: abbreviation,
            value: abbreviation,
            selected: abbreviation === defaultUserValues.state,
          };
        })
        .sort(),
    ];
  }, [defaultUserValues.state]);

  const form = useForm({
    mode: 'onChange',
    resolver: zodResolver(stepTwoSchema(t)),
    defaultValues: defaultUserValues,
  });

  const {
    watch,
    clearErrors,
    trigger,
    formState: { isValid },
  } = form;

  const dateOfBirth = watch(StepTwoFormKeys.dateOfBirth);
  const showGuardianInputs = isUnder18YearsOld(dateOfBirth);

  // Trigger validation after form is populated with defaultUserValues
  useEffect(() => {
    if (user) {
      trigger();
    }
  }, [user, trigger]);

  // TODO: Check if there is a way to move this to the schema itself.
  useEffect(() => {
    if (!showGuardianInputs) {
      clearErrors([StepTwoFormKeys.parentOrGuardian, StepTwoFormKeys.relationshipOfGuardian]);
    }
  }, [clearErrors, showGuardianInputs]);

  const onSubmit = form.handleSubmit((data) => {
    setStepTwoData(data);
    goToNextStep();
  });

  return (
    <FormProvider {...form}>
      <form onSubmit={onSubmit} noValidate>
        <Section title={t('OAS.Booking.Section.Patient')}>
          <StepsWrapper>
            <FormInput
              id={CELEBRUS_PATIENT_FIRST_NAME_ID}
              label={t('OAS.Booking.FirstName.Label')}
              name={StepTwoFormKeys.firstName}
              required
            />
            <FormInput
              id={CELEBRUS_PATIENT_LAST_NAME_ID}
              label={t('OAS.Booking.LastName.Label')}
              name={StepTwoFormKeys.lastName}
              required
            />
            <FormDatePicker
              id={CELEBRUS_PATIENT_DOB_ID}
              label={t('OAS.Booking.DateOfBirth.Label')}
              name={StepTwoFormKeys.dateOfBirth}
              style={{ width: '100%' }}
              required
              calendar={false}
              min="1900-01-01"
              max={getMaxCalendarDate()} // Prevents bug with future dates for now
            />
            <div className="mt-3">
              <FormRadio
                label={t('OAS.Booking.Gender.Label')}
                name={StepTwoFormKeys.gender}
                radioOptions={GENDER_OPTIONS}
                required
              />
            </div>
            {showGuardianInputs && (
              <>
                <div className="mt-3">
                  <FormInput
                    label={t('OAS.Booking.ParentOrGuardian.Label')}
                    name={StepTwoFormKeys.parentOrGuardian}
                    required={showGuardianInputs}
                  />
                </div>
                <div className="mt-3">
                  <FormInput
                    label={t('OAS.Booking.RelationshipOfGuardian.Label')}
                    name={StepTwoFormKeys.relationshipOfGuardian}
                    required={showGuardianInputs}
                  />
                </div>
              </>
            )}
          </StepsWrapper>
        </Section>
        <Section title={t('OAS.Booking.Section.Address')}>
          <StepsWrapper>
            <FormInput
              id={CELEBRUS_PATIENT_ADDRESS_ID}
              label={t('OAS.Booking.AddressOne.Label')}
              name={StepTwoFormKeys.address1}
              required
            />
            <FormInput
              id={CELEBRUS_PATIENT_ADDRESS2_ID}
              label={t('OAS.Booking.AddressTwo.Label')}
              name={StepTwoFormKeys.address2}
            />
          </StepsWrapper>
          <StepsWrapper className="mt-3">
            <FormInput
              id={CELEBRUS_PATIENT_CITY_ID}
              label={t('OAS.Booking.City.Label')}
              name={StepTwoFormKeys.city}
              required
            />
            <FormSelect
              name={StepTwoFormKeys.state}
              filter
              label={t('OAS.Booking.State.Label')}
              className="mt-0"
              items={allStatesAbbreviationItems}
              value={allStatesAbbreviationItems?.find((item) => item.selected)}
              required
              triggerFields={['zip']}
            />
            <FormInput
              id={CELEBRUS_PATIENT_ZIP_ID}
              label={t('OAS.Booking.Zip.Label')}
              name={StepTwoFormKeys.zip}
              required
            />
          </StepsWrapper>
        </Section>
        <Section title={t('OAS.Booking.Section.Contact')}>
          <StepsWrapper>
            <div className="order-1 order-md-0">
              <FormInput
                id={CELEBRUS_PATIENT_EMAIL_ID}
                label={t('OAS.Booking.Email.Label')}
                name={StepTwoFormKeys.email}
                required
              />
            </div>
            <div className="order-0 order-md-1">
              <FormPhoneInput
                id={CELEBRUS_PATIENT_PHONE_ID}
                label={t('OAS.Booking.Phone.Label')}
                name={StepTwoFormKeys.phoneNumber}
                tip={t('OAS.Booking.Phone.Tip')}
                required
              />
            </div>
          </StepsWrapper>
          <EmailTermsAndConditions />
        </Section>
        <Section title={t('OAS.Booking.Section.Carrier')}>
          <StepsWrapper>
            <FormInput
              label={t('OAS.Booking.Carrier.Label')}
              name={StepTwoFormKeys.reasonForReferral}
            />
          </StepsWrapper>
        </Section>
        <NavigationButtons
          isContinueDisabled={!isValid}
          goToPreviousStep={goToPreviousStep}
          continueButtonId={CELEBRUS_CONTINUE_BUTTON_ID_3}
        />
      </form>
    </FormProvider>
  );
};
